import { useRef } from 'react'
import { cn } from '@/lib/utils'
import NotasEditorLazy from './NotasEditorLazy'
import type { NotasEditorHandle } from './NotasEditor'

interface ImagemReuniao {
  tempId: string
  localUrl: string
  uploading?: boolean
}

interface AoVivoNotasPanelProps {
  notas: string
  onNotasChange: (value: string) => void
  isNotasOwner: boolean
  imagens: ImagemReuniao[]
  onUploadImage: (file: File) => void
}

/**
 * Notas panel for ReuniaoAoVivo — editor markdown WYSIWYG (Milkdown) com timestamp,
 * checkbox, paste/drop de imagens e lock multi-device.
 */
export default function AoVivoNotasPanel({
  notas, onNotasChange, isNotasOwner, imagens, onUploadImage,
}: AoVivoNotasPanelProps) {
  const editorRef = useRef<NotasEditorHandle>(null)

  // Drop na área do painel (fora do editor) — dentro do editor o próprio plugin trata.
  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault()
    for (const file of e.dataTransfer.files) if (file.type.startsWith('image/')) onUploadImage(file)
  }

  return (
    <div className="flex-1 flex flex-col p-4 min-h-0" onDragOver={e => e.preventDefault()} onDrop={handleDrop}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] tracking-[0.09em] uppercase text-[#999] font-bold">Anotações</p>
        <div className="flex items-center gap-1.5">
          <button
            onClick={() => editorRef.current?.toggleCheckbox()}
            disabled={!isNotasOwner}
            title="Marcar linha como tarefa (Ctrl+L)"
            aria-label="Marcar linha como tarefa"
            className="flex items-center gap-1 h-6 px-2 rounded-full text-[10px] text-[#555] hover:text-[#ccc] border border-white/[0.07] hover:border-white/[0.15] bg-transparent transition-colors disabled:opacity-40 disabled:hover:text-[#555] disabled:hover:border-white/[0.07]"
          >
            <span className="material-symbols-outlined text-[#555] text-xs">check_box</span>
            <span className="hidden sm:inline">Tarefa</span>
          </button>
          <button
            onClick={() => editorRef.current?.insertTimestamp()}
            disabled={!isNotasOwner}
            title="Inserir timestamp atual"
            aria-label="Inserir timestamp atual"
            className="flex items-center gap-1 h-6 px-2 rounded-full text-[10px] text-[#555] hover:text-[#ccc] border border-white/[0.07] hover:border-white/[0.15] bg-transparent transition-colors disabled:opacity-40 disabled:hover:text-[#555] disabled:hover:border-white/[0.07]"
          >
            <span className="material-symbols-outlined text-[#555] text-xs">push_pin</span>
            <span className="hidden sm:inline">Marcar</span>
          </button>
        </div>
      </div>
      <div className="flex-1 flex flex-col relative min-h-0">
        <NotasEditorLazy
          ref={editorRef}
          value={notas}
          onChange={onNotasChange}
          editable={isNotasOwner}
          onImageFile={onUploadImage}
          ariaLabel="Anotações da reunião"
          placeholder="Digite suas anotações… o markdown formata sozinho"
          className={cn(
            'notas-editor flex-1 min-h-0 rounded-xl overflow-hidden bg-white/[0.02] border transition-colors',
            isNotasOwner ? 'border-white/[0.05] focus-within:border-white/[0.09]' : 'border-white/15',
          )}
        />
        {!isNotasOwner && (
          <div role="status" aria-live="polite" className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/60 backdrop-blur-sm">
            <span className="material-symbols-outlined text-[#888] text-xs">lock</span>
            <span className="text-[10px] text-[#aaa]">Editando em outro dispositivo</span>
          </div>
        )}
      </div>
      {imagens.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-3 mt-1">
          {imagens.map(img => (
            <div key={img.tempId} className="relative rounded-lg overflow-hidden border border-white/10" style={{ width: 48, height: 48 }}>
              <img src={img.localUrl} alt="Imagem da reunião" className="w-full h-full object-cover" />
              {img.uploading && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <span className="material-symbols-outlined text-white text-xs">progress_activity</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="hidden sm:block text-[10px] text-[#666] mt-2">
        Ctrl+L tarefa · Marcar timestamp · cole ou arraste imagens
      </p>
    </div>
  )
}
